import {
  ReactElement,
  useEffect,
  useLayoutEffect,
  useState,
  useRef,
} from "react";
import { useForm } from "react-hook-form";
import { t } from "@lingui/core/macro";

import { Form } from "_/components/form";
import { FormTextAreaField } from "_/components/text-area-field";
import { PermissionTest } from "_/components/permission-test";
import { permissions } from "_/data/rbac/definitions";
import { Modal, SubmitCancelButtons } from "_/components/modal";
import { Icon } from "_/components/icon";
import { useAddToast } from "_/components/toasts";
import { Project, Revision, useUpdateRevision } from "_/data/projects";

import * as S from "./styled";

// Matches the server's `RevisionUpdate.instructions` validation
// (`crates/common/src/models/revision.rs`).
const MAX_INSTRUCTIONS_LENGTH = 2048;

/**
 * Free-text instructions for whoever runs the print, shown/edited from the
 * Prepare tab's project info panel. Read-only for users without
 * `Projects::WRITE`.
 */
export const OperatorInstructions = ({
  project,
  revision,
}: {
  project: Project;
  revision: Revision;
}): ReactElement => {
  const [showEditModal, setShowEditModal] = useState(false);
  const [isExpanded, setIsExpanded] = useState(false);
  const [isClamped, setIsClamped] = useState(false);
  const valueRef = useRef<HTMLDivElement>(null);
  const updateRevision = useUpdateRevision();
  const addToast = useAddToast();
  const hasInstructions = revision.instructions !== null;

  const { control, handleSubmit, reset } = useForm({
    defaultValues: { instructions: revision.instructions ?? "" },
  });

  // Reset the form if the underlying revision changes (e.g. a different
  // revision was selected, or a new revision was created).
  useEffect(() => {
    reset({ instructions: revision.instructions ?? "" });
    setIsExpanded(false);
  }, [revision.id, revision.instructions, reset]);

  // Detect whether the (collapsed) text is actually being clamped, so the
  // "Show more" toggle only appears when there's more to show. This only
  // needs to run while collapsed, since the clamped height is what
  // determines whether there's hidden content.
  useLayoutEffect(() => {
    if (isExpanded) return;
    const el = valueRef.current;
    setIsClamped(!!el && el.scrollHeight > el.clientHeight);
  }, [revision.id, revision.instructions, isExpanded]);

  function closeModal() {
    reset({ instructions: revision.instructions ?? "" });
    setShowEditModal(false);
  }

  function submit(data: { instructions: string }) {
    updateRevision.mutate(
      {
        projectId: project.id,
        revision: { ...revision, instructions: data.instructions || null },
      },
      {
        onSuccess: closeModal,
        onError: () =>
          addToast({
            kind: "danger",
            title: t`components.controls.prepare.save-instructions-error`,
          }),
      }
    );
  }

  return (
    <>
      <S.ProjectInfoItem>
        <S.ProjectInfoLabelRow>
          <S.ProjectInfoLabel>{t`components.controls.prepare.operator-instructions`}</S.ProjectInfoLabel>
          <PermissionTest requiredPermissions={permissions.projects.write}>
            <S.EditButton
              type="button"
              aria-label={t`common.edit`}
              onClick={() => setShowEditModal(true)}
            >
              <Icon variant="Edit" />
            </S.EditButton>
          </PermissionTest>
        </S.ProjectInfoLabelRow>
        <S.InstructionsValueWrapper>
          {hasInstructions ? (
            <S.InstructionsValue ref={valueRef} $expanded={isExpanded}>
              {revision.instructions}
            </S.InstructionsValue>
          ) : (
            <S.EmptyInstructionsValue>
              ({t`components.controls.prepare.no-operator-instructions`})
            </S.EmptyInstructionsValue>
          )}
          {hasInstructions && isClamped && !isExpanded && (
            <S.InstructionsFade />
          )}
        </S.InstructionsValueWrapper>
        {hasInstructions && (isClamped || isExpanded) && (
          <S.ShowMoreToggle
            type="button"
            $expanded={isExpanded}
            onClick={() => setIsExpanded((expanded) => !expanded)}
          >
            {isExpanded
              ? t`components.controls.prepare.show-less`
              : t`components.controls.prepare.show-more`}
            <Icon variant="ExpandMore" />
          </S.ShowMoreToggle>
        )}
      </S.ProjectInfoItem>
      <Modal
        open={showEditModal}
        title={t`components.controls.prepare.operator-instructions`}
        onClose={updateRevision.isPending ? undefined : closeModal}
      >
        <Form onSubmit={handleSubmit(submit)}>
          <FormTextAreaField
            control={control}
            name="instructions"
            placeholder={t`components.controls.prepare.operator-instructions-placeholder`}
            rules={{
              maxLength: {
                value: MAX_INSTRUCTIONS_LENGTH,
                message: t`components.controls.prepare.operator-instructions-max-length ${MAX_INSTRUCTIONS_LENGTH}`,
              },
            }}
          />
          <SubmitCancelButtons
            onCancel={closeModal}
            disableSubmit={updateRevision.isPending}
            disableCancel={updateRevision.isPending}
          />
        </Form>
      </Modal>
    </>
  );
};
